import { ChangeDetectionStrategy, Component, ErrorHandler, Type, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, from, map, of, switchMap, take, timeout } from 'rxjs';
import { FooterMenu } from './interfaces/footer-menu.interface';
import { FooterMobileLoadState } from './interfaces/footer-mobile-load-state.interface';
import { Menu } from './consts/footer-menu.const';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '@grotem/grotem.box.angular.auth-lib';
import { EnvironmentService } from '@services/utils/environment.service';
import { PlatformService } from '@services/utils/platform.service';
import { BuildType } from '@shared/enums/build.type';
import { Platform } from '@shared/enums/platform.enum';

// A stalled chunk request falls back to the regular footer instead of an empty block.
const FOOTER_MOBILE_LOAD_TIMEOUT_MS = 10_000;

@Component({
  selector: 'b2b-portal-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FooterComponent {
  menu: FooterMenu[] = [];

  private readonly tabletFooterMenuColumnsCount = 2;
  private readonly platform = inject(PlatformService).platform;
  private readonly errorHandler = inject(ErrorHandler);
  // The blue mobile footer is drawn for RU/KZ only; the TR footer keeps its own layout.
  private readonly useMobileLayout = computed(
    () => this.platform() === Platform.Mobile && !this.environmentBuildService.isBuildType(BuildType.tr),
  );
  // Loaded on first use, so the mobile footer and PrimeNG Accordion stay out of the initial bundle.
  // Declared after `useMobileLayout`, which it reads while the class initialises.
  private readonly footerMobileLoadState = toSignal(
    toObservable(this.useMobileLayout).pipe(
      filter(Boolean),
      take(1),
      // The timeout covers only the chunk request, not the wait for the mobile layout.
      switchMap(() => from(import('./components/footer-mobile/footer-mobile.component')).pipe(timeout(FOOTER_MOBILE_LOAD_TIMEOUT_MS))),
      map(({ FooterMobileComponent }): FooterMobileLoadState => ({ component: FooterMobileComponent, failed: false })),
      // A failed or stalled chunk (offline, stale deploy) is reported and the regular footer is shown
      // instead; toSignal would otherwise rethrow the error on every change detection that reads it.
      catchError((error: unknown) => {
        this.errorHandler.handleError(error);

        return of<FooterMobileLoadState>({ component: null, failed: true });
      }),
    ),
    { initialValue: { component: null, failed: false } },
  );

  protected readonly BuildTypesEnum = BuildType;
  protected readonly Platform = Platform;
  protected readonly paymentTRComponent = signal<Type<unknown> | null>(null);
  protected readonly footerMobileComponent = computed(() => this.footerMobileLoadState().component);
  protected readonly showMobileFooter = computed(() => this.useMobileLayout() && !this.footerMobileLoadState().failed);

  get canUseTabletColumnsLayout(): boolean {
    return this.menu.length === this.tabletFooterMenuColumnsCount;
  }

  get shouldShowMenu(): boolean {
    return this.authService.hasValidToken() && this.environmentBuildService.isBuildType(BuildType.ru);
  }

  protected readonly environment = environment;

  constructor(
    private authService: AuthService,
    private environmentBuildService: EnvironmentService,
  ) {
    this.menu = this.shouldShowMenu ? Menu : [];

    if (this.environmentBuildService.isBuildType(BuildType.tr)) {
      void this.loadPaymentTR();
    }
  }

  private async loadPaymentTR(): Promise<void> {
    const { PaymentTRComponent } = await import('../payment/payment-tr.component');

    this.paymentTRComponent.set(PaymentTRComponent);
  }
}
